import React, { useState } from 'react';
import axios from 'axios';
import {
  Gift, Mail, Loader2, CheckCircle2, ArrowRight, ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ClaimDiscountButton
 *
 * Renders a "Get my exclusive discount" CTA that opens a small email-capture
 * dialog. On submit:
 *   - POSTs to /api/affiliate-partners/{slug}/claim
 *   - Backend emails the discount code to the buyer + records the claim
 *   - We open the affiliate redirect in a new tab so the buyer doesn't have
 *     to wait for the email to land before continuing to the partner site
 *
 * The actual code (e.g. "JAKE20OFF") is NEVER rendered in the UI — it lives
 * in the DB and is delivered out-of-band by email. This keeps personalised
 * codes private while still giving the buyer the discount.
 */
export default function ClaimDiscountButton({ partner }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!partner?.has_private_discount) return null;

  const submit = async (e) => {
    e?.preventDefault?.();
    const trimmed = (email || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      toast.error('Please enter a valid email');
      return;
    }
    try {
      setSubmitting(true);
      const res = await axios.post(
        `${API_URL}/api/affiliate-partners/${partner.slug}/claim`,
        { email: trimmed },
      );
      if (!res.data?.ok) {
        // Partner has no code configured — fall through to a normal click
        if (res.data?.go_path) {
          window.open(`${API_URL}${res.data.go_path}`, '_blank', 'noopener,noreferrer');
        }
        toast(res.data?.message || 'Redirecting…');
        setOpen(false);
        return;
      }
      setDone(true);
      toast.success(res.data?.message || 'Code on the way');
      // Open the affiliate redirect in a new tab so the buyer's flow continues
      if (res.data?.go_path) {
        window.open(`${API_URL}${res.data.go_path}`, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send the code — try again?');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setOpen(false);
    setTimeout(() => {
      setEmail('');
      setDone(false);
    }, 250);
  };

  return (
    <>
      <Button
        data-testid="claim-discount-button"
        onClick={() => setOpen(true)}
        className="bg-gradient-to-r from-[#00F0FF] to-[#00FF94] text-black font-bold h-14 px-7 text-base rounded-xl hover:opacity-95"
      >
        <Gift className="w-5 h-5 mr-2" />
        Get my exclusive discount
      </Button>

      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : reset())}>
        <DialogContent
          data-testid="claim-discount-modal"
          className="bg-neutral-950 border border-white/10 text-white sm:max-w-md"
        >
          {!done ? (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl flex items-center gap-2">
                  <Gift className="w-5 h-5 text-[#00F0FF]" />
                  Claim your {partner.name} discount
                </DialogTitle>
                <DialogDescription className="text-white/60">
                  {partner.private_discount_description ||
                    `Get your exclusive ${partner.name} discount.`}
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={submit} className="space-y-4 mt-2">
                <div>
                  <label
                    htmlFor="claim-email"
                    className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block"
                  >
                    Email — we'll send your code here
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40 pointer-events-none" />
                    <Input
                      data-testid="claim-discount-email-input"
                      id="claim-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      autoFocus
                      className="bg-neutral-900 border-white/10 text-white pl-9"
                    />
                  </div>
                </div>

                <div className="flex items-start gap-2 text-xs text-white/50">
                  <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-white/40" />
                  <span>
                    One-time email — we won't send marketing without your
                    permission. Your code is sent privately so only you see it.
                  </span>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="claim-discount-submit"
                  className="w-full bg-[#00F0FF] hover:bg-[#00F0FF]/90 text-black font-bold h-12 rounded-lg"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : null}
                  {submitting ? 'Sending…' : 'Send my code'}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-4" data-testid="claim-discount-success">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#00FF94]/15 mb-4">
                <CheckCircle2 className="w-8 h-8 text-[#00FF94]" />
              </div>
              <h2 className="text-xl font-bold mb-1">Check your inbox</h2>
              <p className="text-sm text-white/60 mb-5">
                Your code is on its way to <span className="text-white">{email}</span>.
                {' '}We've also opened {partner.name} in a new tab so you can
                continue your flow.
              </p>
              <Button
                onClick={reset}
                className="bg-[#00F0FF] text-black hover:bg-[#00F0FF]/90"
              >
                Got it
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
